<template>
    <div class="show-group">
        <b-tabs
            class="group-show-tab apprentice-tab"
            v-model="activeTab"
            content-class="group-show-content"
            nav-wrapper-class="tab-content-wrapper"
            @input="updateTabIndex">
            <template #tabs-end>
                <div class="last-tab order-0 order-md-4">
                    <design-2021-group-show-header-buttons-component
                        v-if="groupTreeIn.length >= 1 && groupTreeIn[0] !== null"
                        :group-in="groupIn"
                        :seasons-in="seasonsIn"
                        :courses-in="coursesIn"
                        :projects-in="items"
                        :group-tree-in="groupTreeIn">
                    </design-2021-group-show-header-buttons-component>
                </div>
            </template>

            <b-tab
                v-if="[2, 4, 6, 7].includes(groupIn.type_id) || (groupIn.roadmap && groupIn.roadmap_id)"
                :title="$t('group.progress')"
                title-item-class="progress-tab order-1 order-md-0">
                <design-2021-group-progress-component
                    v-if="[2, 6, 7].includes(groupIn.type_id)"
                    :group-data-in="dataIn"
                    :group-in="groupIn"
                    :student-per-projects="studentsPerProjects">
                </design-2021-group-progress-component>

                <design-2021-group-progress-paper-component
                    v-else
                    :items-in="items">
                </design-2021-group-progress-paper-component>
            </b-tab>

            <b-tab
                v-if="groupIn.portfolios.length"
                :title="$t('group.results')"
                title-item-class="results-tab order-5 order-md-3">
                <design-2021-group-results-component :group-in="groupIn"></design-2021-group-results-component>
            </b-tab>

            <b-tab
                v-if="isGroupPairTeacher()"
                :title="$t('group.groups')"
                title-item-class="group-pair-management-tab order-4 order-md-3">
                <design-2021-group-pair-check-component
                    :group-in="groupIn"
                    :group-pair-groups-in="groupPairGroupsIn"
                    :is-group-admin="groupIn.permissions.coordinator ? 1 : 0"
                    :user-in="$authUser">
                </design-2021-group-pair-check-component>
            </b-tab>

            <b-tab
                :title="$t('group.students')"
                title-item-class="students-tab order-2 order-md-1"
                @click="getInternships">
                <template v-slot:title>
                    {{ $t('group.students') }}
                    <span
                        class="notification-bubble total-messages-count messages-count-bubble"
                        v-if="studentsMessagesTotalCount !== 0">
                        {{ studentsMessagesTotalCount }}
                    </span>
                </template>
                <design-2021-group-assignment-edit-component
                    :group-in="groupIn"
                    :is-student="isStudent"
                    :student-per-projects="studentsPerProjects"
                    :projects-in="projectsIn"
                    :internships-in="internships"
                    @items-changed="itemsChanged($event)"
                    @reset-total-messages-count="resetMessagesCount"
                    :google-map-api="googleMapApi">
                </design-2021-group-assignment-edit-component>
            </b-tab>

            <b-tab
                v-if="!groupIn.permissions.coordinator"
                :title="$t('group.messages')"
                title-item-class="order-3 order-md-2">
                <design-2021-project-show-message-component
                    :project-type-id="groupIn.id"
                    :messages-in="messagesIn"
                    :profile-image-in="profileImageIn"
                    :advanced-option="true"
                    :canRespond="false">
                </design-2021-project-show-message-component>
            </b-tab>

            <b-tab
                v-if="groupIn.permissions.coordinator"
                :title="$t('group.tools')"
                title-item-class="tools-tab order-3 order-md-2">
                <design-2021-group-tools-component
                    :group-id="groupIn.id"
                    :group-type-id="groupIn.type_id"
                    :files-in="filesIn"
                    :messages-in="messagesIn"
                    :permissions="groupIn.permissions"
                    :agenda-items-in="agendaItemsIn"
                    :is-student="isStudent"
                    :is-teacher="isTeacher"
                    :profile-image-in="profileImageIn">
                </design-2021-group-tools-component>
            </b-tab>

            <b-tab
                v-if="groupIn.permissions.coordinator"
                :title="$t('group.settings')"
                title-item-class="settings-tab order-4 order-md-3">
                <design-2021-group-settings-component
                    :active-tab-in="showActiveTab()"
                    :group-in="groupIn"
                    :roadmaps-in="roadmapsIn"
                    :ratings-in="ratingsIn"
                    :questionnaires-in="questionnairesIn"
                    :has-las="hasLas"
                    :courses-in="coursesIn"
                    :group-options-availability="this.groupOptionsAvailability">
                </design-2021-group-settings-component>
            </b-tab>

            <b-tab
                v-if="groupIn.permissions.coordinator && hasSkillTree && groupIn.type_id !== 6"
                :title="$t('group.skills')"
                title-item-class="settings-tab order-4 order-md-3">
                <!-- Why not for internships?-->
                <design-2021-group-show-rubric-survey-component
                    :group-in="groupIn"></design-2021-group-show-rubric-survey-component>
            </b-tab>

            <b-tab
                v-if="groupIn.type_id === 7"
                :title="$t('group.lesson_preparations')"
                title-item-class="lesson-manual-tab order-6 order-md-3">
                <design-2021-group-lesson-manual-component
                    :group-in="groupIn"
                    :lessons-in="lessonsIn">
                </design-2021-group-lesson-manual-component>
            </b-tab>

            <b-tab
                v-if="$authUser.isAdmin() && groupIn.type_id === 4"
                :title="$t('group.admin')"
                title-item-class="admin-tab order-5 order-md-4">
                <design-2021-group-magazine-admin-component
                    :group-in="groupIn"
                    :forced-active-tab="forcedActiveTab">
                </design-2021-group-magazine-admin-component>
            </b-tab>
        </b-tabs>
    </div>
</template>

<script>
import Vue from 'vue';

export default {
    name: 'design-2021-group-show-component',
    props: [
        'dataIn',
        'groupIn',
        'filesIn',
        'messagesIn',
        'agendaItemsIn',
        'roadmapsIn',
        'ratingsIn',
        'groupOptionsAvailability',
        'questionnairesIn',
        'hasLas',
        'isStudent',
        'isTeacher',
        'projectsIn',
        'coursesIn',
        'seasonsIn',
        'groupTreeIn',
        'profileImageIn',
        'groupPairGroupsIn',
        'googleMapApi',
        'lessonsIn',
    ],
    data() {
        return {
            items: this.projectsIn,
            activeTab: 0,
            forcedActiveTab: null,
            studentsMessagesTotalCount: 0,
            hasSkillTree: false,
            studentsPerProjects: [],
            internships: [],
        };
    },
    methods: {
        showActiveTab() {
            const name = `session-${this.$authUser.id}-group-${this.groupIn.id}-SettingsTab`;

            if (localStorage.getItem(name) !== null && this.forcedActiveTab === null) {
                return parseInt(localStorage.getItem(name));
            }

            if (this.forcedActiveTab !== null) {
                return this.forcedActiveTab;
            }
        },

        updateTabIndex(tabIndex) {
            this.activeTab = tabIndex;
            const name = `session-${this.$authUser.id}-group-${this.groupIn.id}-MainTab`;
            localStorage.setItem(name, tabIndex);
        },

        itemsChanged(items) {
            this.items = items;
        },

        resetMessagesCount(count) {
            if (this.studentsMessagesTotalCount !== 0) {
                this.studentsMessagesTotalCount -= count;
            }
        },

        isGroupPairTeacher() {
            const is_group_admin = this.groupIn.users.some((user) => user.user_id === this.$authUser.id);
            const is_teacher_of_group = this.groupIn.group_pair_teachers.some(
                (groupTeacher) => groupTeacher.user_id === this.$authUser.id,
            );

            return this.$authUser.isTeacher() && !is_group_admin && is_teacher_of_group;
        },

        async getInternships() {
            this.axios.get(this.routes.groupInternships(this.groupIn.id)).then(
                (response) => {
                    this.internships = response.data;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
    },

    created() {
        const name = `session-${this.$authUser.id}-group-${this.groupIn.id}-MainTab`;
        if (localStorage.getItem(name)) {
            this.activeTab = parseInt(localStorage.getItem(name));
            if (this.activeTab === 1) {
                this.getInternships();
            }
        }

        if (document.referrer) {
            const qString = document.referrer.split('/');

            if (qString[qString.length - 1] === 'create' && qString[qString.length - 2] === 'group') {
                if (this.groupIn.type_id === 2 || this.groupIn.type_id === 3) {
                    this.activeTab = 3;
                }

                if (this.groupIn.type_id === 1 || this.groupIn.type_id === 4) {
                    this.activeTab = 2;
                }

                this.forcedActiveTab = 1;
            }
        }
    },
    mounted() {
        if (this.projectsIn.length) {
            for (const unreadMessages of this.projectsIn) {
                this.studentsMessagesTotalCount += unreadMessages.messages_count;
            }
        }

        this.axios.get(this.routes.getRubricSurvey()).then(
            (response) => {
                if (response.data.skillTrees.length > 0) {
                    this.hasSkillTree = true;
                }
            },
            (error) => {
                this.alert.ajaxError(error);
            },
        );

        if (this.projectsIn.length) {
            for (const project of this.projectsIn) {
                let students = [];
                for (const student of project.students) {
                    if (this.studentsPerProjects.length === 0) {
                        this.studentsPerProjects.push({
                            id: student.id,
                            name: student.full_name,
                            groups: [project.group.name],
                            project_id: project.id,
                        });
                    } else {
                        let found = false;
                        for (const user of this.studentsPerProjects) {
                            if (user.id === student.id && user.project_id === project.id) {
                                user.groups.push(project.group.name);
                                found = true;
                            }
                        }

                        if (!found) {
                            this.studentsPerProjects.push({
                                id: student.id,
                                name: student.full_name,
                                groups: [project.group.name],
                                project_id: project.id,
                            });
                        }
                    }
                }
            }
        }
    },
};
</script>

<style scoped lang="scss">
.show-group {
    .group-show-buttons {
        .sub-group-select {
            flex: 1 0 auto;

            .child-group-select {
                width: 100%;
            }
        }
    }
}

.group-show-tab ::v-deep {
    .nav-tabs {
        align-items: flex-end;
        flex-wrap: wrap;

        .progress-tab {
            margin-right: 0;
        }

        .nav-item {
            border-radius: 4px;
            flex: 1 0 100%;

            &:first-child {
                .nav-link {
                    padding-left: map-get($grid-gutter-spacing, 'fifteen');
                }
            }
        }

        .nav-link {
            padding: map-get($grid-gutter-spacing, 'ten') 0 map-get($grid-gutter-spacing, 'ten')
                map-get($grid-gutter-spacing, 'fifteen');

            color: $white;

            &:after {
                display: none;
            }
        }

        .last-tab {
            display: flex;
            flex: 1;
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .group-show-tab ::v-deep {
        .nav-tabs {
            align-items: baseline;

            .nav-item {
                border-radius: 0;
                background-color: transparent;
                flex: 0 0 auto;

                &:not(:last-of-type) {
                    margin-bottom: 0;
                }

                &:first-child {
                    padding-left: 0;
                }
            }

            .nav-link {
                padding: 0 map-get($grid-gutter-spacing, 'fifteen');
                color: var(--primary);

                &:after {
                    display: block;
                }
            }

            .last-tab {
                margin-bottom: 0;
                align-self: center;
            }
        }
    }
}
</style>
